import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import { CalendarDays, Plus, Pencil, Trash2 } from 'lucide-react';

const STATUS_CFG = {
  Active: { bg: 'bg-white border-emerald-200', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700', text: 'text-slate-900' },
  Upcoming: { bg: 'bg-white border-blue-200', dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700', text: 'text-slate-900' },
  Closed: { bg: 'bg-slate-50 border-slate-200', dot: 'bg-slate-400', badge: 'bg-slate-100 text-slate-600', text: 'text-slate-500' }
};

const IntakeManagement = () => {
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newIntake, setNewIntake] = useState({ name: '', start_date: '', end_date: '', status: 'Active', admission_target: '' });

  const fetchIntakes = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/system-settings/intakes');
      setIntakes(res.data.periods || []);
    } catch (e) {
      console.error('Failed to fetch intakes', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIntakes(); }, []);

  const addIntake = async () => {
    if (!newIntake.name.trim()) { toast.error('Intake name is required'); return; }
    try {
      setSaving(true);
      await apiClient.post('/system-settings/intakes', newIntake);
      toast.success('Intake period added');
      setShowAdd(false);
      setNewIntake({ name: '', start_date: '', end_date: '', status: 'Active', admission_target: '' });
      fetchIntakes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add intake period');
    } finally { setSaving(false); }
  };

  const updateIntake = async () => {
    if (!editing?.name?.trim()) { toast.error('Intake name is required'); return; }
    try {
      setSaving(true);
      await apiClient.put(`/system-settings/intakes/${editing.id}`, editing);
      toast.success('Intake period updated');
      setEditing(null);
      fetchIntakes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update intake period');
    } finally { setSaving(false); }
  };

  const deleteIntake = async (id) => {
    if (!window.confirm('Are you sure you want to delete this intake period?')) return;
    try {
      await apiClient.delete(`/system-settings/intakes/${id}`);
      toast.success('Intake period deleted');
      fetchIntakes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete intake period');
    }
  };

  const patchIntake = async (intake, patch, successMsg) => {
    try {
      await apiClient.put(`/system-settings/intakes/${intake.id}`, { ...intake, ...patch });
      if (successMsg) toast.success(successMsg);
      fetchIntakes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update intake');
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="intake-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
            <CalendarDays className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Intakes</h1>
            <p className="text-sm text-slate-500">Manage enrollment periods</p>
          </div>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 self-start sm:self-auto" data-testid="add-intake-btn">
          <Plus size={16} className="mr-2" /> Add Intake
        </Button>
      </div>

      <Card>
        <CardHeader className="p-4 sm:p-6 border-b">
          <CardTitle className="text-base sm:text-lg">Intake Periods</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Add, activate or close your enrollment periods. Team-wise / Country-wise breakdowns are now toggled directly on the dashboard's Intake Performance card.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6">
          {loading ? (
            <div className="text-center py-8 text-slate-500"><i className="fas fa-spinner fa-spin mr-2"></i>Loading intake periods...</div>
          ) : intakes.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <CalendarDays size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No intake periods configured</p>
              <p className="text-xs text-slate-400">Click "Add Intake" to create your first intake period</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {intakes.map((intake) => {
                const status = intake.status || (intake.is_active ? 'Active' : 'Closed');
                const cfg = STATUS_CFG[status] || STATUS_CFG.Active;
                return (
                  <div
                    key={intake.id}
                    className={`flex flex-col md:flex-row md:items-center justify-between p-3 sm:p-4 rounded-lg border gap-3 ${cfg.bg}`}
                    data-testid={`intake-row-${intake.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dot}`}></div>
                      <div className="min-w-0">
                        <p className={`font-medium text-sm sm:text-base truncate ${cfg.text}`}>{intake.name}</p>
                        {(intake.start_date || intake.end_date) && (
                          <p className="text-[10px] sm:text-xs text-slate-500 truncate">
                            {intake.start_date && `From: ${intake.start_date}`}
                            {intake.start_date && intake.end_date && ' | '}
                            {intake.end_date && `To: ${intake.end_date}`}
                          </p>
                        )}
                        {(intake.admission_target !== null && intake.admission_target !== undefined && intake.admission_target !== '') && (
                          <p className="text-[10px] sm:text-xs font-medium text-emerald-600 truncate" data-testid={`intake-target-${intake.id}`}>
                            🎯 Target: {intake.admission_target} admissions
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 md:justify-end">
                      {/* Status */}
                      <select
                        value={status}
                        onChange={(e) => patchIntake(intake, { status: e.target.value }, `Intake marked as ${e.target.value}`)}
                        className={`text-[11px] sm:text-xs font-medium px-2 py-1.5 rounded-md border cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-300 ${cfg.badge}`}
                        data-testid={`status-select-${intake.id}`}
                      >
                        <option value="Active">Active</option>
                        <option value="Upcoming">Upcoming</option>
                        <option value="Closed">Closed</option>
                      </select>

                      <Button variant="ghost" size="sm" onClick={() => setEditing({ ...intake, status })} className="h-8 w-8 p-0" data-testid={`edit-intake-${intake.id}`}>
                        <Pencil size={15} className="text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteIntake(intake.id)} className="h-8 w-8 p-0" data-testid={`delete-intake-${intake.id}`}>
                        <Trash2 size={15} className="text-red-600" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Intake Period</DialogTitle>
            <DialogDescription>Create a new intake period for student enrollment</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="intake-name">Intake Name *</Label>
              <Input id="intake-name" placeholder="e.g., Spring 2026, Fall 2026" value={newIntake.name} onChange={(e) => setNewIntake({ ...newIntake, name: e.target.value })} data-testid="new-intake-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="intake-start">Start Date (Optional)</Label>
                <Input id="intake-start" type="date" value={newIntake.start_date} onChange={(e) => setNewIntake({ ...newIntake, start_date: e.target.value })} data-testid="new-intake-start-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="intake-end">End Date (Optional)</Label>
                <Input id="intake-end" type="date" value={newIntake.end_date} onChange={(e) => setNewIntake({ ...newIntake, end_date: e.target.value })} data-testid="new-intake-end-input" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="intake-status">Status</Label>
              <select id="intake-status" value={newIntake.status} onChange={(e) => setNewIntake({ ...newIntake, status: e.target.value })} className="text-sm px-3 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:ring-1 focus:ring-emerald-300" data-testid="new-intake-status-select">
                <option value="Active">Active</option>
                <option value="Upcoming">Upcoming</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="intake-target">Admission Target (Optional)</Label>
              <Input id="intake-target" type="number" min="0" placeholder="e.g., 125 — company-wide goal for this intake" value={newIntake.admission_target} onChange={(e) => setNewIntake({ ...newIntake, admission_target: e.target.value })} data-testid="new-intake-target-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={addIntake} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-new-intake-btn">
              {saving ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</> : <><Plus size={16} className="mr-2" />Add Intake</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Intake Period</DialogTitle>
            <DialogDescription>Update intake period details</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="edit-intake-name">Intake Name *</Label>
                <Input id="edit-intake-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-intake-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-intake-start">Start Date</Label>
                  <Input id="edit-intake-start" type="date" value={editing.start_date || ''} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} data-testid="edit-intake-start-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-intake-end">End Date</Label>
                  <Input id="edit-intake-end" type="date" value={editing.end_date || ''} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} data-testid="edit-intake-end-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-intake-status">Status</Label>
                  <select id="edit-intake-status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full text-sm px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-1 focus:ring-emerald-300" data-testid="edit-intake-status-select">
                    <option value="Active">Active</option>
                    <option value="Upcoming">Upcoming</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-intake-target">Admission Target</Label>
                  <Input id="edit-intake-target" type="number" min="0" placeholder="Company-wide goal" value={editing.admission_target ?? ''} onChange={(e) => setEditing({ ...editing, admission_target: e.target.value })} data-testid="edit-intake-target-input" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={updateIntake} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-intake-btn">
              {saving ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntakeManagement;
